import TinyLlamaWebGpuRuntime, { GenerateOptions } from "@/llm/tinyllamaWebGpuRuntime";

export type LocalLLMState =
  | "idle"
  | "webgpu_not_supported"
  | "downloading"
  | "initializing"
  | "ready"
  | "error";

export type LocalLLMStatus = {
  state: LocalLLMState;
  progress?: { loaded: number; total: number; stage?: string };
  error?: string;
  webGpuInfo?: { adapterName?: string; limits?: any };
  diagnostics?: { hasWebGPU: boolean; crossOriginIsolated: boolean; secureContext: boolean; protocol: string; isIframe: boolean };
};

type Config = { modelId?: string };

// Supported model IDs in @mlc-ai/web-llm
const FALLBACK_MODELS = [
  "Llama-3.2-1B-Instruct-q4f32_1-MLC",
  "Phi-3.1-mini-4k-instruct-q4f32_1-MLC",
];

export class LocalTinyLlamaManager {
  private runtime: TinyLlamaWebGpuRuntime | null = null;
  private status: LocalLLMStatus = { state: "idle" };
  private listeners = new Set<(s: LocalLLMStatus) => void>();
  private config: Config;

  constructor(config: Config = {}) {
    this.config = config;
  }

  getStatus(): LocalLLMStatus {
    return this.status;
  }

  subscribe(listener: (status: LocalLLMStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  private setStatus(next: LocalLLMStatus) {
    this.status = next;
    this.listeners.forEach((fn) => fn(next));
  }

  private async tryInitWithModel(modelId: string, diag: LocalLLMStatus["diagnostics"], info?: LocalLLMStatus["webGpuInfo"]) {
    this.runtime = new TinyLlamaWebGpuRuntime(modelId);
    const baseUrl = `https://canxp.ai/models/${modelId}/`;

    // App config: pin model assets to your base URL (trailing slash required)
    const appConfig = {
      model_list: [
        {
          model_id: modelId,
          model_url: baseUrl
        }
      ]
      // NOTE: omit model_lib_map; let WebLLM use its packaged wasm
    } as any;

    await this.runtime.init({
      modelId,
      onProgress: (loaded, total, stage) => {
        this.setStatus({ state: "downloading", progress: { loaded, total, stage }, webGpuInfo: info, diagnostics: diag });
      },
      // DO NOT set model_lib or model_lib_map; avoid referencing a non-existent wasm file
      appConfig
    });
  }

  async init(): Promise<void> {
    const diag = {
      hasWebGPU: TinyLlamaWebGpuRuntime.isWebGpuSupported(),
      crossOriginIsolated: (globalThis as any).crossOriginIsolated === true,
      secureContext: (globalThis as any).isSecureContext === true,
      protocol: typeof location !== "undefined" ? location.protocol : "unknown",
      isIframe: typeof window !== "undefined" ? window.self !== window.top : false,
    };

    if (!diag.hasWebGPU) {
      this.setStatus({ state: "webgpu_not_supported", diagnostics: diag });
      return;
    }

    const info = await TinyLlamaWebGpuRuntime.getWebGpuInfo();
    this.setStatus({ state: "downloading", webGpuInfo: info, diagnostics: diag });

    const candidates = [
      ...(this.config.modelId ? [this.config.modelId] : []),
      ...FALLBACK_MODELS,
    ];

    let lastErr: any = null;
    for (const model of candidates) {
      try {
        await this.tryInitWithModel(model, diag, info);
        this.setStatus({ state: "initializing", webGpuInfo: info, diagnostics: diag });
        this.setStatus({ state: "ready", webGpuInfo: info, diagnostics: diag });

        // NEW: background pre-warm to compile GPU kernels and reduce first-token latency
        const schedulePrewarm = () => {
          const ric = (typeof window !== "undefined" && (window as any).requestIdleCallback) ? (window as any).requestIdleCallback : null;
          const runPrewarm = () => {
            this.runtime?.generate({
              prompt: "ok",
              maxTokens: 1,
              temperature: 0.0,
              topP: 0.1,
              stopSequences: ["\n"],
            }).catch(() => {
              // Silently ignore pre-warm errors
            });
          };
          if (ric) {
            ric(runPrewarm);
          } else {
            setTimeout(runPrewarm, 1000);
          }
        };
        schedulePrewarm();

        return;
      } catch (err: any) {
        lastErr = err;
        // If the model isn't found in appConfig, continue to next candidate
        const msg = String(err?.message || err);
        const isModelNotFound = msg.includes("ModelNotFoundError") || msg.includes("Cannot find model record");
        if (!isModelNotFound) break; // Non-model error: stop trying others
      }
    }

    // If we reach here, initialization failed
    this.setStatus({ state: "error", error: lastErr?.message || String(lastErr), webGpuInfo: info, diagnostics: diag });
    throw lastErr;
  }

  isReady(): boolean {
    return this.status.state === "ready";
  }

  async generate(options: GenerateOptions): Promise<string> {
    if (!this.runtime || !this.isReady()) {
      throw new Error("Local LLM is not ready");
    }
    return this.runtime.generate(options);
  }

  async dispose(): Promise<void> {
    await this.runtime?.dispose();
    this.setStatus({ state: "idle" });
  }
}

// Singleton instance for app-wide use
export const localLLM = new LocalTinyLlamaManager({ modelId: "Llama-3.2-1B-Instruct-q4f32_1-MLC" });